import { memo, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import isEqual from 'lodash/isEqual'
import { useSelector } from "react-redux";
import { selectQuickWidgetConfig, selectQuickWidgets } from "../../selectors/quickWidgets";
import useDraggable from "../../hooks/useDraggable";
import { alpha, Box, Checkbox, Divider, FormControlLabel, IconButton, MenuItem, Paper, Popover, Stack, Typography, useTheme } from '@mui/material'
import { useStyles } from './styles'
import { makeSelectQuickModeEnabled, selectUserPreferences } from "../../selectors/user";
import { useDispatch } from "react-redux";
import { QuickWidgetTypes, updateQuickWidgetConfig, updateUserPreferencesInState } from "../../reducer/user.reducer";
import CloseIcon from '@mui/icons-material/Close'
import useResizeable from "../../hooks/useResizeable";
import SettingsOutlinedIcon from "../../assets/icons/SettingOutlined";
import { setOpenQuickTradeSettingsDialog } from "../../reducer/quickTrades.reducer";

export type QuickWidgetDialogProps = {
    title: React.ReactNode,
    widgetType: QuickWidgetTypes,
    onDragStart?: (widgetType: QuickWidgetTypes) => void
    onDragEnd?: (widgetType: QuickWidgetTypes) => void
}

export type WidgetStyle = {
    left: number,
    bottom: number,
    height: number,
    width: number
}

const QuickWidgetDialog: React.FC<PropsWithChildren<QuickWidgetDialogProps>> = ({ title, widgetType, children }) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const { classes, cx } = useStyles();
    const quickWidgets = useSelector(selectQuickWidgets)
    const widgetConfig = useSelector(selectQuickWidgetConfig(widgetType));
    const { position: widgetPosition, dimensions } = widgetConfig!
    const { position, containerRef, handleRef, isDragging } = useDraggable<HTMLDivElement, any>(document.body)
    const { containerRef: resizeContainerRef, handleRef: resizeHandleRef,
        isResizing, originalDimensions, delta } = useResizeable<HTMLDivElement, HTMLDivElement>(200, 42)
    const QuickWidgetMode = useSelector(makeSelectQuickModeEnabled(widgetType))

    const [widgetStyle, setWidgetStyle] = useState<WidgetStyle>({
        left: widgetPosition?.left ?? 0,
        bottom: widgetPosition?.bottom ?? 0,
        ...dimensions
    })
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const userPreferences = useSelector(selectUserPreferences);

    const { quickWidgets: quickWidgetPreferences = {} } = userPreferences;

    const previousStyle = useRef<{ left: number, bottom: number, width: number, height: number }>(undefined);

    useEffect(() => {
        const style: WidgetStyle = {
            left: widgetPosition?.left ?? 0,
            bottom: widgetPosition?.bottom ?? 0,
            ...dimensions
        }
        if (isDragging && position) {
            style.left = position.left;
            style.bottom = position.bottom;
            setWidgetStyle(style)
        } else if (isResizing && delta && originalDimensions) {
            style.bottom -= delta.y
            style.height = originalDimensions.height + delta.y
            style.width = originalDimensions.width + delta.x
            setWidgetStyle(style)
        }

    }, [widgetPosition, isDragging, position, quickWidgetPreferences, isResizing, delta, originalDimensions, dimensions])

    useEffect(() => {
        let timeout: number;
        if ((widgetStyle && !isDragging && !isResizing) && (!previousStyle.current || !isEqual(previousStyle.current, widgetStyle))) {
            timeout = window.setTimeout(() => {
                dispatch(updateQuickWidgetConfig({
                    widgetType,
                    config: {
                        position: {
                            left: widgetStyle.left,
                            bottom: widgetStyle.bottom
                        },
                        dimensions: {
                            width: widgetStyle.width,
                            height: widgetStyle.height
                        }, QuickmodeEnabled: false

                    }
                }))
                previousStyle.current = widgetStyle;
            }, 100)
        }
        return () => {
            clearTimeout(timeout)
        }
    }, [widgetType, widgetStyle, isDragging, isResizing, dispatch])

    const onCloseClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        const updatedWidgetPrefs = {
            ...(userPreferences.quickWidgets || {}),
            [widgetType]: {
                ...(quickWidgets?.[widgetType] || {}),
                enabled: false,
            },
        };
        dispatch(updateUserPreferencesInState({
            ...userPreferences,
            quickWidgets: updatedWidgetPrefs,
        }));
    }, [widgetType, quickWidgets, userPreferences, dispatch]);

    const onMouseDown = useCallback(() => {
        const updatedWidgets = { ...(userPreferences.quickWidgets || {}) };

        const widget = updatedWidgets[widgetType];
        if (!widget) return;
        const currentMaxOrder = Math.max(
            ...Object.values(updatedWidgets).map((w) => w?.order || 0)
        );
        if (widget.order === currentMaxOrder) {
            let occurences = 0
            Object.values(updatedWidgets).forEach(w=>{
                if(w.order===widget.order) occurences++
            })
            if(occurences===1) return;
        }
        updatedWidgets[widgetType] = {
            ...widget,
            order: currentMaxOrder + 1,
        };

        dispatch(updateUserPreferencesInState({
            ...userPreferences,
            quickWidgets: updatedWidgets,
        }));
    }, [dispatch, widgetType, userPreferences]);


    const sxProps = useMemo(
        () => ({
            ...widgetStyle,
            zIndex: widgetConfig?.order,
            border: `1px solid ${theme.palette.divider}`
        }),
        [widgetStyle, widgetConfig]
    )

    const handleQuickModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        dispatch(updateQuickWidgetConfig({
            widgetType,
            config: {
                QuickmodeEnabled: event.target.checked,
            },
        }));
    };
    const handleQuickModeToggle = (event: React.MouseEvent<HTMLElement>) => {
        if(widgetType === QuickWidgetTypes.QUICK_TRADE) {
            dispatch(setOpenQuickTradeSettingsDialog({
                settingsDialogOpen: true,
                dialogMode: 'view',
                editTemplateId: undefined
            }))
            handleClose()
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    return <Paper ref={containerRef} sx={sxProps} className={classes.container} elevation={5} onMouseDown={onMouseDown}>
        <div className={classes.resizeContainer} ref={resizeContainerRef}>
            <Stack direction="column" sx={{height: '100%'}}>
                <Paper ref={handleRef} className={cx(classes.heading, { [classes.dragging]: isDragging })} sx={{
                    width: '100%',
                    padding: 1, backgroundColor: theme.palette.mode === 'light' ? theme.palette.background.default : alpha(theme.palette.background.default, 0.1),

                }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography fontSize={14}>
                            {title as any}
                        </Typography>

                        <Box display="flex" alignItems="center" gap={2} flexShrink={0} minWidth="40px">
                            {[QuickWidgetTypes.ORDER, QuickWidgetTypes.POSITION, QuickWidgetTypes.QUICK_TRADE].includes(widgetType) ? (
                                <>
                                    <IconButton
                                        size="small"
                                        className={classes.settingsButton}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={handleQuickModeToggle}
                                        sx={{ p: 0.5 }}
                                    >
                                        <SettingsOutlinedIcon style={{ fontSize: '14px' }} />
                                    </IconButton>
                                    <Popover
                                        open={open}
                                        anchorEl={anchorEl}
                                        onClose={handleClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        style={{ zIndex: 9999999 }}
                                    >
                                        <MenuItem>
                                            <FormControlLabel
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontSize: '13px',
                                                    margin: 0,
                                                }}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={QuickWidgetMode}
                                                        onChange={handleQuickModeChange}
                                                    />
                                                }
                                                label={
                                                    <Box component="span" sx={{ fontSize: '13px', lineHeight: 1 }}>
                                                        Enable Quick Trade Mode
                                                    </Box>
                                                }
                                            />
                                        </MenuItem>
                                    </Popover>
                                </>) : null}
                            <IconButton
                                size="small"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={onCloseClick}
                                className={classes.closeButton}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </Paper>
                <Divider orientation="horizontal" />
                <Paper className={classes.content}>
                    {children as any}
                </Paper>
            </Stack>
            <div ref={resizeHandleRef} className={classes.resizeHandle} />
        </div>
    </Paper>;
}

export default memo(QuickWidgetDialog);